/**
 * Phase 26 — Entity-only Storage Health (meta + entity_* Integrität).
 */

import { countEntityStoresByType } from './entityAggregateStore';
import {
  isEntityStoreNoKvBlobEnabled,
  readBusinessPayloadMeta,
  writeBusinessPayloadMeta
} from './businessPayloadMeta';
import { getCurrentBusinessId } from '../../utils/sharedCore';

/**
 * @param {string} businessId
 */
export async function verifyEntityOnlyStorageHealth(businessId) {
  if (!isEntityStoreNoKvBlobEnabled()) {
    return { ok: false, skipped: true, reason: 'no_kv_blob_disabled' };
  }
  if (!businessId) {
    return { ok: false, skipped: true, reason: 'missing businessId' };
  }

  const meta = await readBusinessPayloadMeta(businessId);
  const counts = await countEntityStoresByType(businessId);
  const entityTotal = Object.values(counts || {}).reduce(
    (sum, n) => sum + Number(n || 0),
    0
  );

  const { getEntityBusinessInfo } = await import('./entityAggregateStore');
  const businessInfo = await getEntityBusinessInfo(businessId);
  const hasBusinessInfo =
    businessInfo && typeof businessInfo === 'object' && Object.keys(businessInfo).length > 0;

  const issues = [];
  if (!meta.noKvBlob) issues.push('payload_meta_not_migrated');
  if (meta.payloadVersion <= 0) issues.push('payload_version_zero');
  if (entityTotal === 0 && !hasBusinessInfo) issues.push('entity_stores_empty');

  return {
    ok: issues.length === 0,
    businessId: String(businessId),
    meta,
    entityCounts: counts,
    entityTotal,
    issues
  };
}

/**
 * Repariert fehlende meta-Flags nach NO_KV_BLOB-Migration.
 * @param {string} businessId
 */
export async function repairEntityOnlyStorageMeta(businessId) {
  const health = await verifyEntityOnlyStorageHealth(businessId);
  if (health.skipped) return health;
  if (health.ok) return { ...health, repaired: false };

  const meta = health.meta;
  if (health.entityTotal > 0 && (!meta.noKvBlob || meta.payloadVersion <= 0)) {
    await writeBusinessPayloadMeta(businessId, {
      payloadVersion: meta.payloadVersion > 0 ? meta.payloadVersion : 1,
      payloadUpdatedAt: meta.payloadUpdatedAt || new Date().toISOString(),
      noKvBlob: true
    });
    return {
      ...(await verifyEntityOnlyStorageHealth(businessId)),
      repaired: true
    };
  }

  return { ...health, repaired: false };
}

/** Startup-Health für aktuelles Business. */
export async function runEntityOnlyStorageStartupHealth() {
  if (!isEntityStoreNoKvBlobEnabled()) {
    return { skipped: true, reason: 'no_kv_blob_disabled' };
  }
  const businessId = getCurrentBusinessId({ allowFallback: true });
  if (!businessId) return { skipped: true, reason: 'no_business' };

  const result = await repairEntityOnlyStorageMeta(businessId);
  if (process.env.NODE_ENV === 'development' && result.repaired) {
    console.info('[Storage Phase 26] Entity-only meta repaired:', businessId);
  }
  return result;
}
